import { gql } from '@common/hooks';
import { HistoryInfoFragment, HistoryInfoFragmentType } from '@common/graphql';
import { InvoiceInstallmentFragmentType } from '.';

export type InvoiceStateType =
  | 'void'
  | 'draft'
  | 'open'
  | 'paid'
  | 'rejected'
  | 'resolved'
  | 'paid_off_platform';

export const InvoiceListItemFragment = gql`
  fragment InvoiceListItemFragment on WorksInvoice {
    id
    code
    totalCents
    payPeriodStartDate
    payPeriodEndDate
    createdAt
    state
    stateChangedAt
    openedDisputeCount
    canVoid {
      value
    }
    canMarkPaid {
      value
    }
    canOpenUp {
      value
    }
    worksReverseInvoices {
      id
      supplier {
        id
        name
      }
    }
  }
`;

export type InvoiceListItemFragmentType = {
  id: string;
  code: string;
  totalCents: number;
  payPeriodStartDate: string;
  payPeriodEndDate: string;
  createdAt: string;
  state: InvoiceStateType;
  stateChangedAt: string;
  openedDisputeCount: number;
  canVoid: {
    value: boolean;
  };
  canMarkPaid: {
    value: boolean;
  };
  canOpenUp: {
    value: boolean;
  };
  worksReverseInvoices: {
    id: string;
    supplier: { id: string; name: string };
  }[];
};

export const InvoiceReviewFragment = gql`
  fragment InvoiceReviewFragment on WorksInvoice {
    id
    code
    totalCents
    totalHours
    payPeriodStartDate
    payPeriodEndDate
    defaultToBillingAddress
    defaultRemitToBillingAddress
    defaultRemitPaymentInfo
    createdAt
    due
    state
    stateChangedAt
    disputesDifferential
    disputesCountByState {
      state
      count
    }
    canVoid {
      value
    }
    canMarkPaid {
      value
    }
    canOpenUp {
      value
    }
    organization {
      id
      name
      payCodes
    }
    remainingInstallmentBalance
    canCreateInstallment {
      value
    }
    invoiceInstallments {
      id
      paidAt
      paymentAmountCents
      canUpdate {
        value
      }
      canArchive {
        value
      }
    }
    history {
      ...HistoryInfoFragment
    }
  }
  ${HistoryInfoFragment}
`;

export type InvoiceReviewFragmentType = {
  id: string;
  code: string;
  totalCents: number;
  totalHours: number;
  preCalculated: boolean;
  payPeriodStartDate: string;
  payPeriodEndDate: string;
  defaultToBillingAddress: string[];
  defaultRemitToBillingAddress: string[];
  defaultRemitPaymentInfo: string[];
  createdAt: string;
  due: string;
  state: InvoiceStateType;
  stateChangedAt: string;
  disputesDifferential: number;
  disputeClosingDate: string;
  disputesCountByState: [
    {
      state: string;
      count: number;
    },
  ];
  canVoid: {
    value: boolean;
  };
  canMarkPaid: {
    value: boolean;
  };
  canOpenUp: {
    value: boolean;
  };
  canSubmitDispute: {
    value: boolean;
  };
  organization: {
    id: string;
    name: string;
    payCodes: string[];
  };
  canCreateInstallment: {
    value: boolean;
  };
  remainingInstallmentBalance: number;
  invoiceInstallments: InvoiceInstallmentFragmentType[];
  history: HistoryInfoFragmentType[];
};
